import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Tabs } from '@/components/ui/tabs';
import { RolePermissionsTab } from './config/RolePermissionsTab';
import { TaskTypesTab } from './config/TaskTypesTab';
import { ResetCycleTab } from './config/ResetCycleTab';
import { ReminderRulesTab } from './config/ReminderRulesTab';
import { TaskStatusTab } from './config/TaskStatusTab';
import { ReportRulesTab } from './config/ReportRulesTab';

const TAB_KEYS = ['role_permissions', 'task_types', 'reset_cycle', 'reminder_rules', 'task_status', 'report_rules'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TAB_LABELS: Record<TabKey, string> = {
  role_permissions: 'Role Permissions',
  task_types: 'Task Types',
  reset_cycle: 'Reset Cycle',
  reminder_rules: 'Reminder Rules',
  task_status: 'Task Status',
  report_rules: 'Report Rules',
};

export default function TaskConfigPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab') as TabKey | null;
  const [active, setActive] = useState<TabKey>(
    requested && TAB_KEYS.includes(requested) ? requested : 'task_types',
  );

  function onChange(key: string) {
    const k = key as TabKey;
    setActive(k);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (k === 'task_types') next.delete('tab');
      else next.set('tab', k);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/tasks" className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Tasks
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">Task Configuration</h1>
        <p className="text-sm text-muted-foreground">Task types, statuses, and reset / reminder / report rules for the Task module.</p>
      </div>

      <Tabs tabs={TAB_KEYS.map((k) => ({ key: k, label: TAB_LABELS[k] }))} active={active} onChange={onChange} />

      {active === 'role_permissions' && <RolePermissionsTab />}
      {active === 'task_types' && <TaskTypesTab />}
      {active === 'reset_cycle' && <ResetCycleTab />}
      {active === 'reminder_rules' && <ReminderRulesTab />}
      {active === 'task_status' && <TaskStatusTab />}
      {active === 'report_rules' && <ReportRulesTab />}
    </div>
  );
}
